import type { InstanceIdPath } from "doc/options/componentInstanceTree";

export interface TreeFilter<T> {
  matches: (node: T) => boolean;
}

export function combineFilters<T>(...filters: (TreeFilter<T> | null | undefined)[]): TreeFilter<T> {
  const valid = filters.filter((f): f is TreeFilter<T> => f !== null && f !== undefined);
  if (valid.length === 0) {
    return { matches: () => true };
  }
  if (valid.length === 1) {
    return valid[0];
  }
  return { matches: (node) => valid.every((f) => f.matches(node)) };
}

export function subtreeMatches<T extends { children: readonly T[] }>(node: T, filter: TreeFilter<T>): boolean {
  return filter.matches(node) || node.children.some((child) => subtreeMatches(child, filter));
}

/** Returns children where the child or any descendant matches the filter. */
export function getChildrenToRender<T extends { children: readonly T[] }>(node: T, filter: TreeFilter<T>): T[] {
  return node.children.filter((child) => subtreeMatches(child, filter));
}

/** Creates a filter matching nodes whose path exists in activePaths. */
export function createActivePathFilter<T extends { path: InstanceIdPath }>(
  activePaths: Set<InstanceIdPath>,
): TreeFilter<T> {
  return { matches: (node) => activePaths.has(node.path) };
}

export function createContentFilter<T>(hasContent: (node: T) => boolean): TreeFilter<T> {
  return { matches: hasContent };
}

/**
 * Creates a filter matching nodes belonging to the selected reporting unit.
 * Nodes with null reportingUnitId (building-level) are excluded in multi-unit buildings.
 * Only the node matching the selected unit (or its descendants) will match.
 */
export function createReportingUnitFilter<T extends { reportingUnitId: string | null }>(
  selectedReportingUnitId: string | undefined,
): TreeFilter<T> | null {
  if (!selectedReportingUnitId) {
    return null;
  }
  return {
    matches: (node) => node.reportingUnitId === selectedReportingUnitId,
  };
}
